import { Box } from '@interstate/components/Box';
import { Typography } from '@interstate/components/Typography';
import { useSelector } from 'react-redux';
import { formatDollarsAndCents } from '@makemydeal/dr-common-utils';
import { toSplitPercent } from '@makemydeal/dr-offer-redux';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { RESERVE_EMPTY_VALUE, RESERVE_GROSS_LABEL, RESERVE_RATE_RESERVE_LABEL, RESERVE_SECTION_LABEL } from '../constants';

export const RESERVE_INFORMATION_TEST_ID = 'reserve-information';

type ReserveRow = {
    label: string;
    value: number | undefined;
    testId: string;
};

const formatAmount = (value: number | undefined) => (value == null ? RESERVE_EMPTY_VALUE : formatDollarsAndCents(value));

const formatPercent = (value: number) => `${Number(value.toFixed(2))}%`;

const ReserveInformation = () => {
    const totalInterestOnRateMarkup = useSelector(offerReduxSelectors.getTotalInterestOnRateMarkup);
    const rateReserve = useSelector(offerReduxSelectors.getReserveFromInterestParticipation);
    const reservePaid = useSelector(offerReduxSelectors.getReservePaid);

    const splitPercent = toSplitPercent(reservePaid);

    const rows: ReserveRow[] = [
        { label: RESERVE_GROSS_LABEL, value: totalInterestOnRateMarkup, testId: 'gross-reserve' },
        {
            label:
                splitPercent == null
                    ? RESERVE_RATE_RESERVE_LABEL
                    : `${RESERVE_RATE_RESERVE_LABEL} at ${formatPercent(splitPercent)} split`,
            value: rateReserve,
            testId: 'rate-reserve'
        }
    ];

    return (
        <Box data-testid={RESERVE_INFORMATION_TEST_ID} sx={{ marginTop: 3 }}>
            <Typography data-testid="reserve-section-title" variant="h4" sx={{ marginBottom: 1.5 }}>
                {RESERVE_SECTION_LABEL}
            </Typography>
            {rows.map(({ label, value, testId }) => (
                <Box
                    key={testId}
                    data-testid={`${testId}-row`}
                    display="flex"
                    justifyContent="space-between"
                    sx={{
                        paddingY: 1,
                        borderBottom: ({ tokens }) => `1px solid ${tokens.BaseColorGray300}`
                    }}
                >
                    <Typography variant="body-sm" data-testid={`${testId}-label`}>
                        {label}
                    </Typography>
                    <Typography variant="body-sm" data-testid={`${testId}-value`}>
                        {formatAmount(value)}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
};

export default ReserveInformation;
